import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Upload, 
  FileText,
  X,
  Users,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  Download,
  Camera,
  UserPlus
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { NewBadge } from './NewBadge';

const ImportPatientsModal = ({ isOpen, onClose, onSuccess, canBulk = true, onPhotoImport }) => {
  const navigate = useNavigate();
  console.log('🔍 BulkImportModal render - isOpen:', isOpen);
  const { toast } = useToast();
  const [importMethod, setImportMethod] = useState(null); // 'paste' or 'csv'
  const [pasteData, setPasteData] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  useEffect(() => {
    console.log('🔍 BulkImportModal isOpen changed to:', isOpen);
  }, [isOpen]);

  // CSV template with the optional Cell Phone column included so practices
  // see they CAN add it. Phone is never required.
  const templateExample = `firstName,lastName,email,phone
John,Doe,john.doe@email.com,555-123-4567
Jane,Smith,jane.smith@email.com,
Robert,Brown,robert.b@email.com,555-987-6543`;

  const handleDragEnter = useCallback((e) => {
    console.log('🟢 handleDragEnter - dragCounter will increment');
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      console.log('dragCounter:', prev, '→', prev + 1);
      return prev + 1;
    });
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    console.log('🔴 handleDragLeave - dragCounter will decrement');
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => {
      const newCounter = prev - 1;
      console.log('dragCounter:', prev, '→', newCounter);
      if (newCounter === 0) {
        console.log('dragCounter reached 0, setting dragActive = false');
        setDragActive(false);
      }
      return newCounter;
    });
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setDragCounter(0);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];

      // Accept CSV or Excel files
      const nameLower = file.name.toLowerCase();
      const isAccepted =
        nameLower.endsWith('.csv') ||
        nameLower.endsWith('.xlsx') ||
        nameLower.endsWith('.xls') ||
        file.type.includes('csv') ||
        file.type.includes('spreadsheet') ||
        file.type.includes('excel');
      if (!isAccepted) {
        toast({
          title: "Invalid File Type",
          description: "Please upload a CSV or Excel (.xlsx) file",
          variant: "destructive",
        });
        return;
      }

      // Check file size (5 MB limit)
      const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: `File size is ${(file.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed is 5 MB.`,
          variant: "destructive",
        });
        return;
      }

      setSelectedFile(file);
      console.log('✅ File selected:', file.name);
    }
  }, [toast]);

  const handleFileSelect = (event) => {
    console.log('🔍 File select triggered');
    const file = event.target.files[0];
    console.log('🔍 Selected file:', file);
    
    if (file) {
      // Accept CSV or Excel files
      const nameLower = file.name.toLowerCase();
      const isAccepted =
        nameLower.endsWith('.csv') ||
        nameLower.endsWith('.xlsx') ||
        nameLower.endsWith('.xls') ||
        file.type.includes('csv') ||
        file.type.includes('spreadsheet') ||
        file.type.includes('excel');
      if (!isAccepted) {
        console.log('❌ Invalid file type:', file.type);
        toast({
          title: "Invalid File Type",
          description: "Please select a CSV or Excel (.xlsx) file",
          variant: "destructive",
        });
        return;
      }

      // Check file size (5 MB limit)
      const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
      if (file.size > maxSize) {
        console.log('❌ File too large:', file.size);
        toast({
          title: "File Too Large",
          description: `File size is ${(file.size / 1024 / 1024).toFixed(2)} MB. Maximum allowed is 5 MB.`,
          variant: "destructive",
        });
        return;
      }

      console.log('✅ Setting selectedFile state');
      setSelectedFile(file);
      setImportMethod('csv');
      console.log('✅ File selected successfully:', file.name);
      toast({
        title: "File Selected",
        description: `${file.name} (${(file.size / 1024).toFixed(2)} KB)`,
      });
    }
  };

  const handleDownloadTemplate = () => {
    const csvBlob = new Blob([templateExample], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(csvBlob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = 'patient_import_template.csv';
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast({
      title: "Template Downloaded",
      description: "patient_import_template.csv",
    });
  };

  const handleCopyTemplate = () => {
    navigator.clipboard.writeText(templateExample);
    toast({
      title: "Template Copied",
      description: "Template copied to clipboard",
    });
  };

  const parsePastedData = (data) => {
    const lines = data.trim().split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      throw new Error('No data to import');
    }

    // NO HEADERS REQUIRED. Accepts 3 or 4 columns:
    // firstName, lastName, email, [phone (optional)]
    // Tab-delimited (Excel paste) and comma-delimited both supported.
    const patients = [];

    for (let i = 0; i < lines.length; i++) {
      const splitChar = lines[i].includes('\t') ? '\t' : ',';
      const values = lines[i].split(splitChar).map(v => v.trim().replace(/^"|"$/g, ''));

      // Skip if this looks like a header row
      const firstValue = (values[0] || '').toLowerCase();
      if (firstValue === 'firstname' || firstValue === 'first name' || firstValue === 'first') {
        continue;
      }

      const patient = {
        firstName: values[0] || '',
        lastName:  values[1] || '',
        email:     values[2] || '',
        phone:     values[3] || '',  // optional 4th column
      };

      // Only require firstName, lastName, and email
      if (patient.firstName && patient.lastName && patient.email) {
        patients.push(patient);
      }
    }

    return patients;
  };

  const handleImportPaste = async () => {
    if (!pasteData.trim()) {
      toast({
        title: "Error",
        description: "Please paste patient data",
        variant: "destructive",
      });
      return;
    }

    if (pasteData.split('\n').filter(line => line.trim()).length > 1001) {
      toast({
        title: "Too Many Rows",
        description: "Maximum 1000 patients allowed (plus header row)",
        variant: "destructive",
      });
      return;
    }

    setImporting(true);

    try {
      const patients = parsePastedData(pasteData);
      
      if (patients.length === 0) {
        throw new Error('No valid patients found in pasted data');
      }

      // Create CSV from parsed data - includes optional phone column
      const headerFields = ['firstName', 'lastName', 'email', 'phone'];
      const headers = headerFields.join(',');
      // CSV-quote phone in case it contains a comma; rest of fields stay clean.
      const esc = (v) => {
        const s = (v ?? '').toString();
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = patients.map(p => `${esc(p.firstName)},${esc(p.lastName)},${esc(p.email)},${esc(p.phone)}`);
      const csvContent = [headers, ...rows].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'pasted_patients.csv', { type: 'text/csv' });

      const results = await practiceApi.importPatientsCSV(file);
      setImportResults(results);

      toast({
        title: "✅ Import Completed Successfully!",
        description: `${results.summary.successfulImports} patient${results.summary.successfulImports !== 1 ? 's' : ''} imported successfully. Check your patient list.`,
        duration: 10000,
      });

      if (results.summary.successfulImports > 0 && onSuccess) {
        onSuccess(results);
      }

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import patients",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleImportCSV = async () => {
    console.log('🔍 CSV Import: handleImportCSV called');
    console.log('🔍 CSV Import: selectedFile:', selectedFile);
    
    if (!selectedFile) {
      console.log('❌ CSV Import: No file selected');
      toast({
        title: "Error",
        description: "Please select a CSV file",
        variant: "destructive",
      });
      return;
    }

    console.log('🔍 CSV Import: Starting import...');
    setImporting(true);

    try {
      console.log('🔍 CSV Import: Calling API...');
      const results = await practiceApi.importPatientsCSV(selectedFile);
      console.log('✅ CSV Import: API response:', results);
      setImportResults(results);

      toast({
        title: "✅ Import Completed Successfully!",
        description: `${results.summary.successfulImports} patient${results.summary.successfulImports !== 1 ? 's' : ''} imported successfully. Check your patient list.`,
        duration: 10000,
      });

      if (results.summary.successfulImports > 0 && onSuccess) {
        onSuccess(results);
      }

    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import Failed",
        description: error.response?.data?.detail || "Failed to import CSV file",
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    console.log('❌ handleClose called - resetting importMethod to null');
    console.trace('handleClose call stack');
    setImportMethod(null);
    setPasteData('');
    setSelectedFile(null);
    setImportResults(null);
    setDragActive(false);
    onClose();
  };

  const handleBack = () => {
    console.log('⬅️ handleBack called - resetting importMethod to null');
    console.trace('handleBack call stack');
    setImportMethod(null);
    setPasteData('');
    setSelectedFile(null);
    setImportResults(null);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div 
        className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center">
            <Users className="h-6 w-6 mr-3 text-blue-600" />
            <h2 className="text-xl font-semibold">Import Patients</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6">
          {!importMethod && !importResults && (
            <>
              {canBulk && (
              <Alert className="mb-6 border-blue-200 bg-blue-50">
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <p className="font-semibold">Choose your import method:</p>
                    <ul className="list-disc list-inside text-sm space-y-1 ml-2">
                      <li><strong>Paste Method:</strong> Up to 1,000 patients (1,001 lines including header)</li>
                      <li><strong>File Upload:</strong> Up to 10,000 patients per file (.csv or .xlsx)</li>
                      <li><strong>File Size Limit:</strong> Maximum 5 MB</li>
                      <li><strong>Required columns:</strong> First Name, Last Name, Email</li>
                      <li><strong>Optional column:</strong> Cell Phone (add a 4th column if you have it — leave blank for patients who don't)</li>
                    </ul>
                    <a
                      href="/patient-import-guide.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="import-guide-pdf-link"
                      className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-white border-2 border-cyan-300 hover:border-cyan-500 hover:bg-cyan-50 rounded-md text-cyan-800 font-bold text-xs transition-colors"
                    >
                      <Download className="h-3.5 w-3.5" />
                      Download full step-by-step PDF guide
                    </a>
                  </div>
                </AlertDescription>
              </Alert>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Option 0: Add one patient at a time */}
                <Card
                  data-testid="import-method-single"
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-emerald-300 hover:border-emerald-500"
                  onClick={() => { handleClose(); navigate('/add-patient?returnTo=/'); }}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <UserPlus className="h-5 w-5 mr-2 text-emerald-600" />
                      Add One Patient
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Type in a single patient. Great for a new patient or your first test (use yourself!)
                    </p>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li>• Name, cell phone and email</li>
                      <li>• Preferred language and dentist</li>
                      <li>• Ready to send instructions right away</li>
                    </ul>
                  </CardContent>
                </Card>
                {canBulk && (<>
                {/* Option 1: Paste Patients */}
                <Card 
                  data-testid="import-method-paste"
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                  onClick={() => setImportMethod('paste')}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <FileText className="h-5 w-5 mr-2 text-blue-600" />
                      Paste Patients
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Copy and paste patient data directly from Excel or CSV
                    </p>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li>• Quick and easy</li>
                      <li>• Up to 1,000 patients</li>
                      <li>• Instant validation</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Option 2: Upload CSV */}
                <Card 
                  data-testid="import-method-csv"
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                  onClick={() => setImportMethod('csv')}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Upload className="h-5 w-5 mr-2 text-green-600" />
                      Upload CSV or Excel File
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Drag and drop or select a .csv or .xlsx file to import
                    </p>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li>• CSV (.csv) and Excel (.xlsx) supported</li>
                      <li>• Up to 10,000 patients</li>
                      <li>• Maximum file size: 5 MB</li>
                    </ul>
                  </CardContent>
                </Card>
                </>)}

                {/* Option 3: Photo, scan or PDF (AI reads a printed list) */}
                <Card
                  data-testid="import-method-photo"
                  className="cursor-pointer hover:shadow-lg transition-shadow border-2 hover:border-blue-500"
                  onClick={() => { handleClose(); onPhotoImport?.(); }}
                >
                  <CardHeader>
                    <CardTitle className="flex items-center text-lg">
                      <Camera className="h-5 w-5 mr-2 text-purple-600" />
                      Photo, Scan or PDF
                      <NewBadge className="ml-2" testId="import-photo-new-badge" />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-3">
                      Take a photo of a printed patient list, or upload a scan or PDF. AI reads it for you
                    </p>
                    <ul className="text-sm text-gray-500 space-y-1">
                      <li>• Prints from Dentrix, Eaglesoft, Open Dental…</li>
                      <li>• Up to 10 pages or photos at once</li>
                      <li>• Review every row before saving</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
              {!canBulk && (
                <p className="mt-4 text-sm text-gray-500" data-testid="import-bulk-permission-note">
                  Paste and CSV/Excel import are available to staff with Settings access. Ask your office manager if you need them.
                </p>
              )}

              {/* Template Download */}
              {canBulk && (
              <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                <h3 className="font-semibold text-sm mb-2 flex items-center">
                  <Download className="h-4 w-4 mr-2" />
                  Need a template?
                </h3>
                <p className="text-sm text-gray-600 mb-3">
                  Download or copy our CSV template with the correct format
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={handleDownloadTemplate}
                    variant="outline"
                    size="sm"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </Button>
                  <Button
                    onClick={handleCopyTemplate}
                    variant="outline"
                    size="sm"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Template
                  </Button>
                </div>
              </div>
              )}
            </>
          )}

          {/* Paste Method */}
          {importMethod === 'paste' && !importResults && (
            <>
              <Alert className="mb-4 border-blue-200 bg-blue-50">
                <FileText className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-semibold mb-1">Paste patient data (NO headers needed)</p>
                  <p className="text-xs">Required: firstName, lastName, email</p>
                  <p className="text-xs">Optional 4th column: phone (cell number) — leave blank for patients without one</p>
                </AlertDescription>
              </Alert>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Patient Data (up to 1,000 patients)
                </label>
                <Textarea
                  value={pasteData}
                  onChange={(e) => setPasteData(e.target.value)}
                  placeholder={templateExample}
                  className="font-mono text-sm h-64"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lines: {pasteData.split('\n').filter(l => l.trim()).length} / 1001 (including header)
                </p>
              </div>

              <div className="flex justify-between">
                <Button onClick={handleBack} variant="outline">
                  Back
                </Button>
                <Button 
                  onClick={handleImportPaste}
                  disabled={importing || !pasteData.trim()}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {importing ? (
                    <>
                      <LoadingSpinner />
                      Importing...
                    </>
                  ) : (
                    'Import Patients'
                  )}
                </Button>
              </div>
            </>
          )}

          {/* CSV Upload Method */}
          {importMethod === 'csv' && !importResults && (
            <>
              <Alert className="mb-4 border-green-200 bg-green-50">
                <Upload className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-semibold mb-1">Drag and drop your CSV file or click to browse</p>
                  <p className="text-xs text-gray-600">Limits: Up to 10,000 patients • Maximum 5 MB file size</p>
                </AlertDescription>
              </Alert>

              {/* Drag and Drop Area */}
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
                  dragActive 
                    ? 'border-blue-500 bg-blue-50' 
                    : selectedFile
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-300 bg-gray-50'
                }`}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  id="csv-file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {selectedFile ? (
                  <>
                    <CheckCircle className="h-12 w-12 mx-auto mb-3 text-green-600" />
                    <p className="font-semibold mb-1">{selectedFile.name}</p>
                    <p className="text-sm text-gray-600 mb-3">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                    <Button
                      onClick={() => setSelectedFile(null)}
                      variant="outline"
                      size="sm"
                    >
                      Remove File
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                    <p className="font-semibold mb-1">
                      Drag and drop CSV file here
                    </p>
                    <p className="text-sm text-gray-600 mb-3">or</p>
                    <Button
                      onClick={() => document.getElementById('csv-file').click()}
                      variant="outline"
                    >
                      Browse Files
                    </Button>
                  </>
                )}
              </div>

              <div className="flex justify-between">
                <Button onClick={handleBack} variant="outline">
                  Back
                </Button>
                <Button 
                  onClick={handleImportCSV}
                  disabled={importing || !selectedFile}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importing ? (
                    <>
                      <LoadingSpinner />
                      Importing...
                    </>
                  ) : (
                    'Import Patients'
                  )}
                </Button>
              </div>
            </>
          )}

          {/* Results */}
          {importResults && (
            <>
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Import Results</h3>
                  <div className="flex gap-2">
                    {importResults.summary.successfulImports > 0 && (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="h-5 w-5 mr-1" />
                        <span className="font-semibold">{importResults.summary.successfulImports} Success</span>
                      </div>
                    )}
                    {importResults.summary.failedImports > 0 && (
                      <div className="flex items-center text-red-600">
                        <XCircle className="h-5 w-5 mr-1" />
                        <span className="font-semibold">{importResults.summary.failedImports} Failed</span>
                      </div>
                    )}
                    {importResults.summary.duplicateEmails > 0 && (
                      <div className="flex items-center text-yellow-600">
                        <AlertTriangle className="h-5 w-5 mr-1" />
                        <span className="font-semibold">{importResults.summary.duplicateEmails} Duplicates</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Failed Imports */}
                {importResults.failedImports && importResults.failedImports.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-red-600 mb-2">Failed Imports:</h4>
                    <div className="max-h-48 overflow-y-auto border rounded p-3 bg-red-50">
                      {importResults.failedImports.map((fail, idx) => (
                        <div key={idx} className="text-sm mb-2 pb-2 border-b last:border-b-0">
                          <p className="font-medium">Row {fail.row}: {fail.data?.firstName} {fail.data?.lastName}</p>
                          <p className="text-red-600">{fail.error}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Duplicate Emails */}
                {importResults.duplicateEmails && importResults.duplicateEmails.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-semibold text-yellow-600 mb-2">Skipped Duplicates:</h4>
                    <div className="max-h-48 overflow-y-auto border rounded p-3 bg-yellow-50">
                      {importResults.duplicateEmails.map((dup, idx) => (
                        <div key={idx} className="text-sm mb-1">
                          {dup}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button onClick={handleClose} className="bg-blue-600 hover:bg-blue-700">
                  Close
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImportPatientsModal;
